(function () {
  'use strict';

  if (customElements.get('video-submission-form')) return;

  const MAX_UPLOAD_ATTEMPTS = 5;
  const STATUS_POLL_INTERVAL = 3000;
  const STATUS_POLL_LIMIT = 400;

  class VideoSubmissionForm extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.beforeUnloadHandler = (event) => {
        if (!this.busy) return;
        event.preventDefault();
        event.returnValue = '';
      };
    }

    connectedCallback() {
      this.form = this.querySelector('[data-video-submission-form]');
      this.fileInput = this.querySelector('[data-video-file]');
      this.fileSummary = this.querySelector('[data-file-summary]');
      this.feedbackRegion = this.querySelector('[data-feedback-region]');
      this.feedbackLabel = this.querySelector('[data-feedback-label]');
      this.progress = this.querySelector('[data-progress]');
      this.progressPercent = this.querySelector('[data-progress-percent]');
      this.submitButton = this.form && this.form.querySelector('button[type="submit"]');

      const configElement = document.getElementById(this.dataset.configId);
      if (!this.form || !this.fileInput || !configElement) return;

      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        this.showStatus('error', 'The upload form is not configured correctly.');
        return;
      }

      this.config.apiBaseUrl = String(this.config.apiBaseUrl || '').replace(/\/+$/, '');
      this.config.acceptedExtensions = (this.config.acceptedExtensions || [])
        .map((extension) => String(extension).trim().toLowerCase())
        .filter(Boolean);

      this.fileInput.addEventListener('change', () => this.handleFileSelection());
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    disconnectedCallback() {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }

    handleFileSelection() {
      const file = this.fileInput.files && this.fileInput.files[0];
      if (!file) {
        this.fileSummary.textContent = this.config.messages.noFile;
        return;
      }

      this.fileSummary.textContent = `${file.name} · ${this.formatBytes(file.size)}`;
      this.clearStatus();
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.busy || !this.form.reportValidity()) return;

      const file = this.fileInput.files && this.fileInput.files[0];
      if (!file) {
        this.fileInput.reportValidity();
        return;
      }
      if (!this.config.apiBaseUrl) {
        this.showStatus('error', 'The upload service has not been configured yet.');
        return;
      }
      if (file.size > Number(this.config.maxFileSize)) {
        this.showStatus('error', this.config.messages.fileTooLarge);
        return;
      }
      if (!this.isAcceptedExtension(file.name)) {
        this.showStatus('error', this.config.messages.unsupportedFile);
        return;
      }

      this.setBusy(true);
      this.setProgress(0, this.config.messages.starting);

      try {
        const created = await this.api('/v1/submissions', {
          method: 'POST',
          body: JSON.stringify(this.createSubmissionPayload(file)),
        });

        await this.uploadResumable(file, created.uploadUrl, Number(created.chunkSize));
        this.setProgress(100, this.config.messages.processing);

        let submission = await this.api(`/v1/submissions/${encodeURIComponent(created.submissionId)}/complete`, {
          method: 'POST',
          body: '{}',
        });
        if (submission.status !== 'completed') {
          submission = await this.pollUntilComplete(created.submissionId);
        }
        if (submission.status !== 'completed') throw new Error('Submission processing did not complete.');

        this.resetAfterSuccess();
        this.showStatus('success', this.config.messages.success);
      } catch (error) {
        const message = error && error.userMessage ? error.userMessage : this.config.messages.error;
        this.showStatus('error', message);
        if (window.turnstile) window.turnstile.reset();
      } finally {
        this.setBusy(false);
      }
    }

    resetAfterSuccess() {
      this.form.reset();
      this.form.querySelectorAll('input, select, textarea').forEach((control) => {
        if (control.type === 'checkbox' || control.type === 'radio') {
          control.checked = false;
        } else if (control.tagName === 'SELECT') {
          control.selectedIndex = 0;
        } else {
          control.value = '';
        }
      });
      this.fileSummary.textContent = this.config.messages.noFile;
      this.progress.hidden = true;
      this.progressPercent.hidden = true;
      this.progress.value = 0;
      this.progressPercent.textContent = '0%';
      if (window.turnstile) window.turnstile.reset();
    }

    createSubmissionPayload(file) {
      const customFields = Array.from(this.querySelectorAll('[data-custom-field]')).map((wrapper) => {
        const control = wrapper.querySelector('input, select, textarea');
        let value = control ? control.value : '';
        if (control && control.type === 'checkbox') value = control.checked;
        return {
          id: wrapper.dataset.fieldId,
          label: wrapper.dataset.fieldLabel,
          value,
        };
      });
      const turnstileInput = this.form.querySelector('[name="cf-turnstile-response"]');

      return {
        fields: {
          name: this.form.elements.name.value.trim(),
          email: this.form.elements.email.value.trim(),
          orderNumber: this.form.elements.order_number.value.trim(),
          termsAccepted: this.form.elements.terms.checked,
          ugcConsentAccepted: this.form.elements.ugc_consent.checked,
          customFields,
        },
        file: {
          name: file.name,
          size: file.size,
          type: this.mimeFromExtension(file.name),
        },
        turnstileToken: turnstileInput ? turnstileInput.value : undefined,
      };
    }

    async uploadResumable(file, uploadUrl, chunkSize) {
      let offset = 0;

      while (offset < file.size) {
        const endExclusive = Math.min(offset + chunkSize, file.size);
        const chunk = file.slice(offset, endExclusive);
        let uploaded = false;
        let lastError;

        for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt += 1) {
          try {
            const result = await this.sendChunk(uploadUrl, chunk, offset, endExclusive - 1, file.size, (loaded) => {
              this.setProgress(((offset + loaded) / file.size) * 100, this.config.messages.uploading);
            });

            if (result.status === 200 || result.status === 201) {
              offset = file.size;
              uploaded = true;
              break;
            }
            if (result.status === 308) {
              offset = this.offsetFromRange(result.range, endExclusive);
              uploaded = true;
              break;
            }
            throw new Error(`Unexpected upload response: ${result.status}`);
          } catch (error) {
            lastError = error;
            if (attempt >= MAX_UPLOAD_ATTEMPTS) break;
            this.setProgress((offset / file.size) * 100, this.config.messages.retrying);
            await this.delay(500 * Math.pow(2, attempt - 1));

            try {
              const status = await this.queryUploadOffset(uploadUrl, file.size);
              if (status.complete) {
                offset = file.size;
                uploaded = true;
                break;
              }
              offset = status.offset;
              if (offset >= endExclusive) {
                uploaded = true;
                break;
              }
              if (offset > 0 && offset !== endExclusive - chunk.size) {
                uploaded = true;
                break;
              }
            } catch (_) {
              // Retry the same chunk when the status probe also encounters a network error.
            }
          }
        }

        if (!uploaded) throw lastError || new Error('Upload failed.');
      }
    }

    sendChunk(uploadUrl, chunk, start, end, total, onProgress) {
      return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('PUT', uploadUrl, true);
        request.timeout = 120000;
        request.setRequestHeader('Content-Type', 'application/octet-stream');
        request.setRequestHeader('Content-Range', `bytes ${start}-${end}/${total}`);
        request.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) onProgress(event.loaded);
        });
        request.addEventListener('load', () => {
          if ([200, 201, 308].includes(request.status)) {
            resolve({ status: request.status, range: request.getResponseHeader('Range') });
          } else {
            reject(new Error(`Upload failed with status ${request.status}.`));
          }
        });
        request.addEventListener('error', () => reject(new Error('Network error while uploading.')));
        request.addEventListener('timeout', () => reject(new Error('Upload request timed out.')));
        request.send(chunk);
      });
    }

    queryUploadOffset(uploadUrl, total) {
      return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open('PUT', uploadUrl, true);
        request.timeout = 30000;
        request.setRequestHeader('Content-Range', `bytes */${total}`);
        request.addEventListener('load', () => {
          if (request.status === 200 || request.status === 201) {
            resolve({ offset: total, complete: true });
            return;
          }
          if (request.status === 308) {
            resolve({
              offset: this.offsetFromRange(request.getResponseHeader('Range'), 0),
              complete: false,
            });
            return;
          }
          reject(new Error(`Could not resume upload (${request.status}).`));
        });
        request.addEventListener('error', () => reject(new Error('Could not check upload status.')));
        request.addEventListener('timeout', () => reject(new Error('Upload status check timed out.')));
        request.send(new Blob([]));
      });
    }

    async pollUntilComplete(submissionId) {
      for (let attempt = 0; attempt < STATUS_POLL_LIMIT; attempt += 1) {
        await this.delay(STATUS_POLL_INTERVAL);
        const submission = await this.api(`/v1/submissions/${encodeURIComponent(submissionId)}`);
        if (submission.status === 'completed') return submission;
        if (submission.status === 'failed') {
          const error = new Error(submission.message || this.config.messages.error);
          error.userMessage = submission.message || this.config.messages.error;
          throw error;
        }
        this.setProgress(100, this.config.messages.processing);
      }
      throw new Error('Submission processing timed out.');
    }

    async api(path, options) {
      const response = await fetch(`${this.config.apiBaseUrl}${path}`, {
        ...options,
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          ...(options && options.headers ? options.headers : {}),
        },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(body.error && body.error.message ? body.error.message : `Request failed (${response.status}).`);
        error.userMessage = body.error && body.error.message;
        throw error;
      }
      return body;
    }

    setBusy(busy) {
      this.busy = busy;
      this.dataset.busy = String(busy);
      if (this.submitButton) {
        this.submitButton.disabled = busy;
        this.submitButton.setAttribute('aria-busy', String(busy));
      }
    }

    setProgress(value, label) {
      const safeValue = Math.max(0, Math.min(100, value));
      this.feedbackRegion.hidden = false;
      this.feedbackRegion.dataset.state = 'info';
      this.feedbackLabel.textContent = label;
      this.progress.hidden = false;
      this.progressPercent.hidden = false;
      this.progress.value = safeValue;
      this.progressPercent.textContent = `${Math.round(safeValue)}%`;
    }

    showStatus(state, message) {
      this.feedbackRegion.hidden = false;
      this.feedbackRegion.dataset.state = state;
      this.feedbackLabel.textContent = message;
      this.progress.hidden = true;
      this.progressPercent.hidden = true;
    }

    clearStatus() {
      this.feedbackRegion.hidden = true;
      this.feedbackLabel.textContent = '';
      this.progress.hidden = true;
      this.progressPercent.hidden = true;
      delete this.feedbackRegion.dataset.state;
    }

    offsetFromRange(range, fallback) {
      const match = range && range.match(/bytes=0-(\d+)/i);
      return match ? Number(match[1]) + 1 : fallback;
    }

    isAcceptedExtension(filename) {
      const extension = filename.includes('.') ? filename.split('.').pop().toLowerCase() : '';
      return this.config.acceptedExtensions.includes(extension);
    }

    mimeFromExtension(filename) {
      const extension = filename.split('.').pop().toLowerCase();
      return {
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        webm: 'video/webm',
        m4v: 'video/x-m4v',
      }[extension] || 'application/octet-stream';
    }

    formatBytes(bytes) {
      if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
      if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
      return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
    }

    delay(milliseconds) {
      return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
    }
  }

  customElements.define('video-submission-form', VideoSubmissionForm);
})();
